export type TakeKind = "gen" | "import" | "rework";

export type FinishPresetId = "spotify" | "apple" | "youtube";
export type TakeListen = "original" | "finished";

export type TakeFinish = {
  preset: FinishPresetId;
  at: string;
  lufs?: number;
  truePeakDb?: number;
};

export type KaraokeBed = {
  at: string;
  source: "dsp" | "gen";
};

export type TakeItem = {
  id: string;
  title: string;
  createdAt: string;
  duration: number;
  seed: number;
  lyrics?: string;
  /** Whisper-aligned LRC for Karaoke (known lyrics matched to sung audio). */
  syncedLyrics?: string;
  caption?: string;
  objectUrl?: string;
  finishedUrl?: string;
  karaokeBedUrl?: string;
  contentType?: string;
  folderId?: string;
  bytes?: number;
  kind?: TakeKind;
  parentId?: string;
  finish?: TakeFinish;
  listen?: TakeListen;
  karaoke?: KaraokeBed;
};

export function rememberSyncedLyrics(list: TakeItem[], id: string, syncedLyrics: string): TakeItem[] {
  const next = list.map((t) => (t.id === id ? { ...t, syncedLyrics } : t));
  saveTakes(next);
  return next;
}

export function finishedBlobKey(id: string): string {
  return `${id}::finished`;
}

export function karaokeBlobKey(id: string): string {
  return `${id}::karaoke`;
}

export type TakeFolder = {
  id: string;
  name: string;
  createdAt: string;
};

export const INBOX_ID = "inbox";
const KEY = "musicforge.takes.v1";
const FOLDER_KEY = "musicforge.folders.v1";
const FOLDER_CUR_KEY = "musicforge.folder.current.v1";
const MAX = 200;
const DB_NAME = "musicforge.takes";
const STORE = "blobs";

export function loadTakes(): TakeItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as TakeItem[];
    return Array.isArray(arr) ? arr.slice(0, MAX) : [];
  } catch {
    return [];
  }
}

export function saveTakes(items: TakeItem[]): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify(
        items.map(({ objectUrl: _o, finishedUrl: _f, karaokeBedUrl: _k, ...rest }) => rest).slice(0, MAX),
      ),
    );
  } catch {
    /* */
  }
}

export function pushTake(list: TakeItem[], take: TakeItem): TakeItem[] {
  const next = [take, ...list.filter((t) => t.id !== take.id)].slice(0, MAX);
  saveTakes(next);
  return next;
}

export function loadFolders(): TakeFolder[] {
  const inbox: TakeFolder = { id: INBOX_ID, name: "Inbox", createdAt: "2026-01-01T00:00:00.000Z" };
  try {
    const raw = localStorage.getItem(FOLDER_KEY);
    const arr = raw ? (JSON.parse(raw) as TakeFolder[]) : [];
    const rest = Array.isArray(arr) ? arr.filter((f) => f?.id && f.id !== INBOX_ID) : [];
    return [inbox, ...rest];
  } catch {
    return [inbox];
  }
}

export function saveFolders(folders: TakeFolder[]): void {
  try {
    localStorage.setItem(
      FOLDER_KEY,
      JSON.stringify(folders.filter((f) => f.id !== INBOX_ID)),
    );
  } catch {
    /* */
  }
}

export function loadCurrentFolderId(): string {
  try {
    return localStorage.getItem(FOLDER_CUR_KEY) || INBOX_ID;
  } catch {
    return INBOX_ID;
  }
}

export function saveCurrentFolderId(id: string): void {
  try {
    localStorage.setItem(FOLDER_CUR_KEY, id);
  } catch {
    /* */
  }
}

export function addFolder(folders: TakeFolder[], name: string): TakeFolder[] {
  const label = name.trim() || "Folder";
  const folder: TakeFolder = {
    id: `fold_${Date.now().toString(36)}`,
    name: label,
    createdAt: new Date().toISOString(),
  };
  const next = [...folders, folder];
  saveFolders(next);
  return next;
}

export function renameFolder(folders: TakeFolder[], id: string, name: string): TakeFolder[] {
  if (id === INBOX_ID) return folders;
  const next = folders.map((f) => (f.id === id ? { ...f, name: name.trim() || f.name } : f));
  saveFolders(next);
  return next;
}

export function removeFolder(
  folders: TakeFolder[],
  takes: TakeItem[],
  id: string,
): { folders: TakeFolder[]; takes: TakeItem[] } {
  if (id === INBOX_ID) return { folders, takes };
  const nextFolders = folders.filter((f) => f.id !== id);
  saveFolders(nextFolders);
  const nextTakes = takes.map((t) => (t.folderId === id ? { ...t, folderId: INBOX_ID } : t));
  saveTakes(nextTakes);
  return { folders: nextFolders, takes: nextTakes };
}

export function moveTakes(list: TakeItem[], ids: string[], folderId: string): TakeItem[] {
  const set = new Set(ids);
  const next = list.map((t) => (set.has(t.id) ? { ...t, folderId } : t));
  saveTakes(next);
  return next;
}

export function takeFolderId(take: TakeItem): string {
  return take.folderId || INBOX_ID;
}

export function formatBytes(n?: number): string {
  if (!n || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatTakeWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fileStem(name: string): string {
  return name.replace(/\.[^.]+$/, "").slice(0, 80) || "Untitled";
}

export function audioDuration(url: string): Promise<number> {
  return new Promise((resolve) => {
    if (typeof Audio === "undefined") {
      resolve(0);
      return;
    }
    const a = new Audio();
    a.preload = "metadata";
    const done = (n: number) => {
      a.removeAttribute("src");
      a.load();
      resolve(n);
    };
    a.onloadedmetadata = () => done(Math.round(a.duration) || 0);
    a.onerror = () => done(0);
    a.src = url;
  });
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

export async function putTakeBlob(id: string, blob: Blob): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(blob, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
  db.close();
}

export async function getTakeBlob(id: string): Promise<Blob | null> {
  const db = await openDb();
  if (!db) return null;
  const blob = await new Promise<Blob | null>((resolve) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(id);
    req.onsuccess = () => resolve((req.result as Blob | undefined) || null);
    req.onerror = () => resolve(null);
  });
  db.close();
  return blob;
}

export async function deleteTakeBlob(id: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
  db.close();
}

export async function hydrateTakeBlobs(items: TakeItem[]): Promise<TakeItem[]> {
  const out: TakeItem[] = [];
  for (const t of items) {
    let next = t;
    if (!next.objectUrl) {
      const blob = await getTakeBlob(t.id);
      if (blob) {
        next = {
          ...next,
          objectUrl: URL.createObjectURL(blob),
          contentType: blob.type || t.contentType,
        };
      }
    }
    if (next.finish && !next.finishedUrl) {
      const finished = await getTakeBlob(finishedBlobKey(t.id));
      if (finished) {
        next = { ...next, finishedUrl: URL.createObjectURL(finished) };
      }
    }
    if (next.karaoke && !next.karaokeBedUrl) {
      const bed = await getTakeBlob(karaokeBlobKey(t.id));
      if (bed) {
        next = { ...next, karaokeBedUrl: URL.createObjectURL(bed) };
      }
    }
    out.push(next);
  }
  return out;
}

export async function rememberTake(
  list: TakeItem[],
  take: TakeItem,
  blob: Blob,
): Promise<TakeItem[]> {
  await putTakeBlob(take.id, blob);
  const next = pushTake(list, { ...take, bytes: take.bytes ?? blob.size, folderId: take.folderId || INBOX_ID });
  const dropped = list.filter((t) => !next.some((n) => n.id === t.id));
  for (const d of dropped) {
    if (d.objectUrl) URL.revokeObjectURL(d.objectUrl);
    if (d.finishedUrl) URL.revokeObjectURL(d.finishedUrl);
    if (d.karaokeBedUrl) URL.revokeObjectURL(d.karaokeBedUrl);
    await deleteTakeBlob(d.id);
    await deleteTakeBlob(finishedBlobKey(d.id));
    await deleteTakeBlob(karaokeBlobKey(d.id));
  }
  return next;
}

export async function dropTake(list: TakeItem[], id: string): Promise<TakeItem[]> {
  return dropTakes(list, [id]);
}

export async function dropTakes(list: TakeItem[], ids: string[]): Promise<TakeItem[]> {
  const set = new Set(ids);
  for (const t of list) {
    if (!set.has(t.id)) continue;
    if (t.objectUrl) URL.revokeObjectURL(t.objectUrl);
    if (t.finishedUrl) URL.revokeObjectURL(t.finishedUrl);
    if (t.karaokeBedUrl) URL.revokeObjectURL(t.karaokeBedUrl);
    await deleteTakeBlob(t.id);
    await deleteTakeBlob(finishedBlobKey(t.id));
    await deleteTakeBlob(karaokeBlobKey(t.id));
  }
  const next = list.filter((t) => !set.has(t.id));
  saveTakes(next);
  return next;
}

export function renameTake(list: TakeItem[], id: string, title: string): TakeItem[] {
  const next = list.map((t) => (t.id === id ? { ...t, title: title.trim() || t.title } : t));
  saveTakes(next);
  return next;
}

export async function rememberFinished(
  list: TakeItem[],
  id: string,
  blob: Blob,
  finish: TakeFinish,
): Promise<TakeItem[]> {
  const cur = list.find((t) => t.id === id);
  if (cur?.finishedUrl) URL.revokeObjectURL(cur.finishedUrl);
  await putTakeBlob(finishedBlobKey(id), blob);
  const finishedUrl = URL.createObjectURL(blob);
  const next = list.map((t) =>
    t.id === id ? { ...t, finishedUrl, finish, listen: "finished" as const } : t,
  );
  saveTakes(next);
  return next;
}

export async function rememberKaraokeBed(
  list: TakeItem[],
  id: string,
  blob: Blob,
  source: KaraokeBed["source"] = "dsp",
): Promise<TakeItem[]> {
  const cur = list.find((t) => t.id === id);
  if (cur?.karaokeBedUrl) URL.revokeObjectURL(cur.karaokeBedUrl);
  await putTakeBlob(karaokeBlobKey(id), blob);
  const karaokeBedUrl = URL.createObjectURL(blob);
  const karaoke: KaraokeBed = { at: new Date().toISOString(), source };
  const next = list.map((t) => (t.id === id ? { ...t, karaokeBedUrl, karaoke } : t));
  saveTakes(next);
  return next;
}
